import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { pickLocalized, useLang, useT } from '../../i18n';
import { api } from '../../lib/api';
import type { MochiEnglishStory, MochiStoryLibrary } from '../../types/mochiEnglish';

type LoadState = 'loading' | 'ready' | 'error';
type CompletionFilter = 'all' | 'unfinished' | 'finished';

export default function MochiStories() {
  const t = useT();
  const { lang } = useLang();
  const [state, setState] = useState<LoadState>('loading');
  const [library, setLibrary] = useState<MochiStoryLibrary | null>(null);
  const [activeCollection, setActiveCollection] = useState('all');
  const [completionFilter, setCompletionFilter] = useState<CompletionFilter>('all');
  const [saving, setSaving] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.mochiEnglish.stories()
      .then((response) => {
        if (cancelled) return;
        setLibrary(response);
        setState('ready');
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : 'unknown error');
        setState('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const completedCount = useMemo(
    () => library?.stories.filter((story) => story.completed).length ?? 0,
    [library],
  );
  const visibleStories = useMemo(() => {
    if (!library) return [];
    return library.stories.filter((story) => {
      const inCollection = activeCollection === 'all' || story.collection_id === activeCollection;
      const inCompletion = completionFilter === 'all'
        || (completionFilter === 'finished' && story.completed)
        || (completionFilter === 'unfinished' && !story.completed);
      return inCollection && inCompletion;
    });
  }, [activeCollection, completionFilter, library]);

  async function setCompleted(story: MochiEnglishStory, completed: boolean) {
    if (!library || saving.has(story.id)) return;
    setSaveError('');
    setSaving((current) => new Set(current).add(story.id));
    setLibrary({
      ...library,
      stories: library.stories.map((candidate) => (
        candidate.id === story.id ? withCompletion(candidate, completed) : candidate
      )),
    });
    try {
      const progress = await api.mochiEnglish.setStoryCompleted(story.id, completed);
      setLibrary((current) => current ? {
        ...current,
        stories: current.stories.map((candidate) => (
          candidate.id === story.id
            ? withCompletion(candidate, progress.completed, progress.completed_at)
            : candidate
        )),
      } : current);
    } catch (reason) {
      setLibrary((current) => current ? {
        ...current,
        stories: current.stories.map((candidate) => (
          candidate.id === story.id
            ? withCompletion(candidate, story.completed, story.completed_at)
            : candidate
        )),
      } : current);
      setSaveError(reason instanceof Error ? reason.message : 'unknown error');
    } finally {
      setSaving((current) => {
        const next = new Set(current);
        next.delete(story.id);
        return next;
      });
    }
  }

  if (state === 'loading') return <p className="font-mono text-sm text-coffee">{t('common.loading')}</p>;
  if (state === 'error' || !library) {
    return <p className="border-y border-rose-200 py-6 font-mono text-sm text-rose-700">{t('mochi.english.stories.error', { error })}</p>;
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <section className="grid gap-px overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-200 md:grid-cols-[1.25fr_0.75fr]">
        <div className="bg-white p-6 md:p-8">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-coffee/65">
            {t('mochi.english.stories.kicker')}
          </p>
          <h3 className="mt-3 text-3xl font-semibold text-cocoa md:text-4xl">
            {t('mochi.english.stories.title', { count: library.total_stories })}
          </h3>
          <p className="mt-3 max-w-2xl leading-relaxed text-coffee">
            {t('mochi.english.stories.intro')}
          </p>
        </div>
        <div className="flex flex-col justify-center bg-emerald-50/70 p-6 md:p-8">
          <p className="font-mono text-4xl font-semibold text-emerald-800">
            {completedCount}<span className="text-xl text-emerald-700/60">/{library.total_stories}</span>
          </p>
          <p className="mt-2 text-sm text-emerald-950/75">{t('mochi.english.stories.finishedCount')}</p>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/80">
            <div
              className="h-full rounded-full bg-emerald-600 transition-[width] duration-500"
              style={{ width: `${(completedCount / library.total_stories) * 100}%` }}
            />
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex max-w-full gap-2 overflow-x-auto pb-2" aria-label={t('mochi.english.stories.collectionFilter')}>
          <FilterButton active={activeCollection === 'all'} onClick={() => setActiveCollection('all')}>
            {t('mochi.english.stories.allCollections')}
          </FilterButton>
          {library.collections.map((collection) => (
            <FilterButton
              key={collection.id}
              active={activeCollection === collection.id}
              onClick={() => setActiveCollection(collection.id)}
            >
              {pickLocalized(collection.title, lang)}
            </FilterButton>
          ))}
        </div>
        <div className="flex flex-wrap gap-2" aria-label={t('mochi.english.stories.completionFilter')}>
          {(['all', 'unfinished', 'finished'] as const).map((filter) => (
            <FilterButton
              key={filter}
              active={completionFilter === filter}
              onClick={() => setCompletionFilter(filter)}
            >
              {t(`mochi.english.stories.filters.${filter}`)}
            </FilterButton>
          ))}
        </div>
        {saveError && (
          <p className="font-mono text-xs text-rose-700">{t('mochi.english.stories.saveError', { error: saveError })}</p>
        )}
      </section>

      {visibleStories.length === 0 ? (
        <p className="border-y border-neutral-200 py-8 text-coffee">{t('mochi.english.stories.empty')}</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {visibleStories.map((story) => {
            const collection = library.collections.find((candidate) => candidate.id === story.collection_id);
            const isSaving = saving.has(story.id);
            return (
              <article
                key={story.id}
                className={`overflow-hidden rounded-2xl border bg-white shadow-soft transition ${story.completed ? 'border-emerald-300' : 'border-neutral-200'}`}
              >
                <div className="p-5">
                  <details className="group min-w-0">
                    <summary className="cursor-pointer list-none focus-visible:outline-none">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-xs uppercase tracking-[0.14em] text-coffee/55">
                            #{story.number} {collection ? `· ${pickLocalized(collection.title, lang)}` : ''}
                          </p>
                          <h4 className={`mt-2 text-xl font-semibold ${story.completed ? 'text-emerald-800' : 'text-cocoa'}`}>
                            {story.title.en}
                          </h4>
                          {lang === 'zh' && <p className="mt-1 text-sm text-coffee" lang="zh-CN">{story.title.zh}</p>}
                        </div>
                        <span aria-hidden className="text-xl text-coffee transition group-open:rotate-45">+</span>
                      </div>
                    </summary>

                    <div className="mt-5 space-y-5 border-t border-neutral-100 pt-5">
                      <p className="whitespace-pre-line text-[0.98rem] leading-7 text-cocoa" lang="en">
                        {story.body.en}
                      </p>
                      <details className="rounded-lg bg-neutral-50 p-3">
                        <summary className="cursor-pointer font-mono text-xs text-coffee">
                          {t('mochi.english.stories.chineseReference')}
                        </summary>
                        <p className="mt-3 whitespace-pre-line text-sm leading-6 text-coffee" lang="zh-CN">
                          {story.body.zh}
                        </p>
                      </details>
                    </div>
                  </details>
                  <button
                    type="button"
                    aria-pressed={story.completed}
                    aria-label={t(
                      story.completed
                        ? 'mochi.english.stories.markUnfinished'
                        : 'mochi.english.stories.markFinished',
                      { title: story.title.en },
                    )}
                    disabled={isSaving}
                    onClick={() => void setCompleted(story, !story.completed)}
                    className={`mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-xl border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-wait disabled:opacity-60 sm:w-auto ${story.completed ? 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700' : 'border-cocoa/25 bg-paper text-cocoa hover:border-cocoa'}`}
                  >
                    {isSaving
                      ? t('mochi.english.stories.saving')
                      : story.completed
                        ? t('mochi.english.stories.finishedButton')
                        : t('mochi.english.stories.markFinishedButton')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function withCompletion(
  story: MochiEnglishStory,
  completed: boolean,
  completedAt?: string,
): MochiEnglishStory {
  const next = { ...story, completed };
  if (completedAt) next.completed_at = completedAt;
  else delete next.completed_at;
  return next;
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`shrink-0 rounded-full border px-3 py-2 text-sm transition ${active ? 'border-cocoa bg-cocoa text-white' : 'border-neutral-200 bg-white text-coffee hover:border-neutral-400'}`}
    >
      {children}
    </button>
  );
}
